// Skin-weight repair for a loaded SkinnedMesh.
//
// glTF allows WEIGHTS_0 to be stored quantized (normalized UNSIGNED_BYTE or
// UNSIGNED_SHORT). Unreal's exporter writes 8-bit, so every weight is rounded
// to a multiple of 1/255 and a vertex's four weights sum to anywhere in
// 0.996..1.004. Linear-blend skinning multiplies the vertex by that sum, so a
// vertex 1.6 m from the origin moves up to ~6 mm along its own position vector
// at REST — a spiked surface. Measured on a MetaHuman face at LOD 0: mean 1.7 mm,
// max 13 mm, 27% of vertices over 5 mm. Blender renormalizes on import, which is
// why the same file looks clean there; three.js does not, and its own
// SkinnedMesh.normalizeSkinWeights() writes back through the SAME quantized
// array, so the result is rounded again and nothing changes.
//
// This replaces the attribute with float32 weights that sum to exactly one, or
// leaves a float attribute that already sums to one untouched.

import * as THREE from 'three'

/**
 * Pure: returns a Float32Array of per-vertex weights summing to one, reading
 * through the attribute's normalization (so quantized storage is decoded).
 * A vertex whose weights sum to zero keeps zeros (there is nothing to scale).
 */
export function renormalizedSkinWeights(attr) {
  const count = attr.count
  const size = attr.itemSize
  const out = new Float32Array(count * size)
  for (let i = 0; i < count; i++) {
    let sum = 0
    for (let k = 0; k < size; k++) sum += attr.getComponent(i, k)
    const scale = sum > 0 ? 1 / sum : 0
    for (let k = 0; k < size; k++) out[i * size + k] = attr.getComponent(i, k) * scale
  }
  return out
}

/** True when the attribute is quantized storage or any vertex's weights miss one by more than eps. */
export function skinWeightsNeedRepair(attr, eps = 1e-4) {
  if (!attr) return false
  if (attr.normalized || !(attr.array instanceof Float32Array)) return true
  const size = attr.itemSize
  for (let i = 0; i < attr.count; i++) {
    let sum = 0
    for (let k = 0; k < size; k++) sum += attr.getComponent(i, k)
    if (sum > 0 && Math.abs(sum - 1) > eps) return true
  }
  return false
}

/**
 * Repairs `mesh.geometry.attributes.skinWeight` in place (new float32
 * attribute) when needed. Returns true when it replaced the attribute.
 */
export function repairSkinWeights(mesh) {
  const geom = mesh?.geometry
  const attr = geom?.attributes?.skinWeight
  if (!attr || !skinWeightsNeedRepair(attr)) return false
  const repaired = new THREE.BufferAttribute(renormalizedSkinWeights(attr), attr.itemSize, false)
  geom.setAttribute('skinWeight', repaired)
  return true
}
